import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../api/context";
import type { Prospect } from "../api/types";
import { useConfig } from "../config";
import { DemoFlow } from "../demo/DemoFlow";
import { DemoShell } from "../demo/DemoShell";
import { resolveParentOrigin } from "../demo/parentOrigin";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const EMBED_KEY = /^ek_[A-Za-z0-9_-]{20,64}$/;

/** `/demo` runs TryLook's own discovery call; `/demo?embed=1&key=…` runs a client's demo inside embed.js's iframe. */
export function DemoPage() {
  useDocumentTitle("Live demo · TryLook");
  const config = useConfig();
  const api = useApi();
  const [params] = useSearchParams();
  const embedded = params.get("embed") === "1";
  const embedKey = embedded ? (params.get("key") ?? "") : config.landingEmbedKey;
  const parentOrigin = embedded ? resolveParentOrigin(window.location, document.referrer, params.get("parent")) : null;

  const start = useCallback(
    (prospect: Prospect) =>
      api.startSession({ embedKey, source: embedded ? "embed" : "landing", parentOrigin, prospect }),
    [api, embedKey, embedded, parentOrigin],
  );

  const close =
    embedded && parentOrigin ? () => window.parent.postMessage({ type: "trylook:close" }, parentOrigin) : undefined;

  return (
    <DemoShell embedded={embedded}>
      {EMBED_KEY.test(embedKey) ? (
        <DemoFlow
          heading={embedded ? "See the product, live" : "Talk to our AI demo guide"}
          intro={
            embedded
              ? "An AI guide will ask what you're working on, show you the parts that matter to you, and can book a call with the team."
              : "It'll ask about your team, show you how TryLook works, and book a call with us if it's a fit. It takes about ten minutes."
          }
          embedded={embedded}
          onStart={start}
          onClose={close}
        />
      ) : (
        <section className="demo-card">
          <h1 className="demo-title">This demo isn't set up correctly</h1>
          <p className="muted">The embed code on this website is missing its key. Please let the site owner know.</p>
        </section>
      )}
    </DemoShell>
  );
}
